import { SEO } from '@/components/common/seo';
import { AnimatedButton } from '@/components/common/animated-button';
import { RadonePricingSection } from '@/pages/portfolio/components/radone-pricing-section';
import { motion } from 'framer-motion';
import { Globe, Zap, Mic, LayoutList, Bot, Shuffle, Monitor, ChevronRight, ArrowRight, type LucideIcon } from 'lucide-react';
import radoneHeroImg from '@/assets/radone/radone-hero.png';

/* ─── Data ─── */

const beforeItems = [
  'Structure done manually, every time',
  'Impressions written from scratch',
  'Clinical reasoning lives in your head',
];

const afterItems = [
  'Observations structured automatically',
  'Impressions evolve with context',
  'Clinical reasoning stays intact',
];

const flowSteps = [
  {
    num: '01',
    title: 'Open RadOne alongside your viewer',
    description:
      'No installation, no integration setup. RadOne runs in the browser - open it next to your PACS, just like any other tab.',
    chips: [
      { label: 'Browser-based', icon: Globe },
      { label: 'Zero setup', icon: Zap },
    ],
  },
  {
    num: '02',
    title: 'Report with full AI assistance',
    description:
      'Dictate or type your observations. RadOne structures findings, refines impressions, surfaces differentials, and answers your clinical questions - in real time.',
    chips: [
      { label: 'Voice input', icon: Mic },
      { label: 'Auto-structure', icon: LayoutList },
      { label: 'AI assistant', icon: Bot },
    ],
  },
  {
    num: '03',
    title: 'Copy into any RIS or PACS',
    description:
      'Your finished report is clean, structured, and ready. Paste it into whichever system your centre uses. RadOne works with everything - because it doesn\'t need to connect to anything.',
    chips: [
      { label: 'System-agnostic', icon: Shuffle },
      { label: 'Any RIS / PACS', icon: Monitor },
    ],
  },
];

const collabCards = [
  {
    question: '"What are the differentials for this appearance?"',
    answer: 'RadOne pulls clinically relevant differentials ranked by likelihood - based on your findings, not generic search results.',
  },
  {
    question: '"Is this finding consistent with the clinical history?"',
    answer: 'Cross-references your observations against the referral context and flags any inconsistencies worth noting.',
  },
  {
    question: '"Make this impression more concise."',
    answer: 'Rewrites while preserving clinical meaning - no generic summarisation, no lost nuance.',
  },
  {
    question: '"Are there any critical findings I should flag?"',
    answer: 'Proactively identifies urgent findings before you sign off and prompts communication where needed.',
  },
];

const worthItems = [
  {
    num: '3x',
    title: 'Faster report completion',
    description: 'Auto-structuring and AI drafting eliminate the repetitive work in every study.',
  },
  {
    num: '↓40%',
    title: 'Reduction in report errors',
    description: 'Context-aware suggestions catch inconsistencies before sign-off.',
  },
  {
    num: '0',
    title: 'Integrations required',
    description: 'Works with every RIS and PACS on the market. No IT involvement needed.',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
};

const Radone = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO canonical="/portfolio/radone" />

      {/* HERO */}
      <motion.section
          className="relative flex flex-col items-start md:items-center pt-12 sm:pt-24 md:pt-20 pb-0 px-4 sm:px-6 md:px-8"
          initial="hidden"
          animate="visible"
          variants={stagger}
        >
          {/* Badge */}
          <motion.div
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[var(--brilliant-azure)]/20 bg-[var(--brilliant-azure)]/5 backdrop-blur-sm mb-10"
            variants={fadeUp}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--brilliant-azure)]" />
            <span className="text-sm font-medium text-muted-foreground">
              AI Reporting Workspace
            </span>
          </motion.div>

          {/* Heading */}
          <motion.h1
            className="font-display text-5xl md:text-6xl lg:text-7xl font-medium text-left md:text-center max-w-4xl leading-[1.1] mb-8 tracking-tight"
            variants={fadeUp}
            style={{
              background: 'linear-gradient(to bottom, var(--foreground), var(--foreground), rgba(255,255,255,0.6))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Your PACS editor
            <br />
            was never built
            <br />
            for <span className="hero-wave-text italic">thinking.</span>
          </motion.h1>

          {/* Description */}
          <motion.p
            className="text-base md:text-lg text-muted-foreground text-left md:text-center max-w-2xl mb-12 leading-relaxed"
            variants={fadeUp}
          >
            RadOne is a standalone AI reporting environment - purpose-built for
            radiologists who want to think better, report faster, and sign off
            with confidence.
          </motion.p>

          {/* Hero image with glow backdrop */}
          <motion.div className="w-full max-w-7xl relative pb-20" variants={fadeUp}>
            {/* Glow — concentrated at the top edge of the image */}
            <div
              className="absolute left-1/2 -translate-x-1/2 w-[80%] -top-[10%] pointer-events-none z-0"
              aria-hidden="true"
            >
              {/* Core bright glow — tight, intense */}
              <div
                className="w-full aspect-[3/1] opacity-70 blur-[60px]"
                style={{
                  background: 'radial-gradient(ellipse 60% 50% at center, var(--brilliant-azure) 0%, var(--royal-blue) 40%, transparent 70%)',
                }}
              />
              {/* Warm spread — wider, softer */}
              <div
                className="absolute inset-0 w-full aspect-[3/1] opacity-40 blur-[100px]"
                style={{
                  background: 'radial-gradient(ellipse 70% 60% at center, var(--sky-aqua) 0%, var(--fresh-sky) 25%, var(--brilliant-azure) 50%, transparent 75%)',
                }}
              />
            </div>

            {/* Image */}
            <div className="relative z-10">
              <img
                src={radoneHeroImg}
                alt="RadOne AI reporting workspace interface"
                className="w-full h-auto rounded-lg shadow-2xl shadow-black/40 border border-white/[0.08]"
                width={1920}
                height={1080}
                loading="eager"
              />
            </div>

            {/* Bottom fade */}
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-background via-background/60 to-transparent pointer-events-none z-20" />
          </motion.div>
      </motion.section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pb-24">
        {/* BEFORE / AFTER */}
        <motion.section
          className="py-24 border-b border-border"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          variants={stagger}
        >
          <motion.div variants={fadeUp}>
            <Tag>The shift</Tag>
          </motion.div>
          <motion.h2
            className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.15] mb-4"
            variants={fadeUp}
          >
            Traditional systems capture
            <br />
            what you say.
            <br />
            <span className="text-[var(--brilliant-azure)] hero-wave-text">
              RadOne understands what you mean.
            </span>
          </motion.h2>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border rounded-2xl overflow-hidden mt-12"
            variants={fadeUp}
          >
            {/* Before */}
            <div className="bg-card p-6 md:p-8">
              <div className="text-xs tracking-[0.08em] uppercase mb-5 font-medium text-muted">
                Before RadOne
              </div>
              <h3 className="text-xl md:text-2xl font-medium leading-[1.3] mb-5 text-muted">
                You type.
                <br />
                The system records.
              </h3>
              <ul className="flex flex-col gap-3">
                {beforeItems.map((item) => (
                  <ContrastItem key={item} variant="before">{item}</ContrastItem>
                ))}
              </ul>
            </div>
            {/* After */}
            <div className="bg-card p-6 md:p-8">
              <div className="text-xs tracking-[0.08em] uppercase mb-5 font-medium text-[var(--brilliant-azure)]">
                With RadOne
              </div>
              <h3 className="text-xl md:text-2xl font-medium leading-[1.3] mb-5 text-foreground">
                You observe.
                <br />
                The system thinks with you.
              </h3>
              <ul className="flex flex-col gap-3">
                {afterItems.map((item) => (
                  <ContrastItem key={item} variant="after">{item}</ContrastItem>
                ))}
              </ul>
            </div>
          </motion.div>
        </motion.section>

        {/* HOW IT WORKS */}
        <motion.section
          className="py-24 border-b border-border"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          variants={stagger}
        >
          <motion.div variants={fadeUp}>
            <Tag>How it works</Tag>
          </motion.div>

          <motion.div className="flex flex-col" variants={stagger}>
            {flowSteps.map((step) => (
              <FlowStep key={step.num} num={step.num} title={step.title}>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                  {step.description}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {step.chips.map((chip) => (
                    <Chip key={chip.label} icon={chip.icon}>{chip.label}</Chip>
                  ))}
                </div>
              </FlowStep>
            ))}
          </motion.div>
        </motion.section>

        {/* AI COLLABORATOR */}
        <motion.section
          className="py-24 border-b border-border"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          variants={stagger}
        >
          <motion.div variants={fadeUp}>
            <Tag>AI assistant</Tag>
          </motion.div>
          <motion.h2
            className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.15] mb-4"
            variants={fadeUp}
          >
            Not a chatbot.
            <br />
            A{' '}
            <span className="hero-wave-text">collaborator.</span>
          </motion.h2>
          <motion.p
            className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mb-4"
            variants={fadeUp}
          >
            Ask anything, mid-report. RadOne knows the context - the study, the
            findings, the patient history. It responds like a knowledgeable
            colleague.
          </motion.p>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-12"
            variants={stagger}
          >
            {collabCards.map((card) => (
              <CollabCard key={card.question} question={card.question} answer={card.answer} />
            ))}
          </motion.div>
        </motion.section>

        {/* WHY RADIOLOGISTS CHOOSE RADONE */}
        <motion.section
          className="py-24 border-b border-border"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          variants={stagger}
        >
          <motion.div variants={fadeUp}>
            <Tag>Why radiologists choose RadOne</Tag>
          </motion.div>
          <motion.h2
            className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.15] mb-2 "
            variants={fadeUp}
          >
            Worth opening.
            <br />
            <span className="text-primary hero-wave-text">
              Every single time.
            </span>
          </motion.h2>
          <motion.p
            className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl"
            variants={fadeUp}
          >
            Radiologists have always known what good reporting looks like. They
            just never had a tool that matched it. Here's what happens when a
            tool nails it.
          </motion.p>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-border rounded-2xl overflow-hidden mt-12"
            variants={fadeUp}
          >
            {worthItems.map((item) => (
              <WorthItem key={item.num} num={item.num} title={item.title}>
                {item.description}
              </WorthItem>
            ))}
          </motion.div>
        </motion.section>

        <RadonePricingSection />
      </div>
    </div>
  );
};

/* ─── Sub-components ─── */

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.08em] uppercase text-muted-foreground rounded-full px-4 py-1.5 mb-6 pl-0">
      <span className="w-1.5 h-1.5 rounded-full bg-[var(--brilliant-azure)]" />
      {children}
      <ChevronRight className="w-4 h-4 text-muted-foreground/60" />
    </span>
  );
}

function ContrastItem({
  variant,
  children,
}: {
  variant: 'before' | 'after';
  children: React.ReactNode;
}) {
  const isBefore = variant === 'before';
  return (
    <li className="text-sm sm:text-base flex items-start gap-2.5 leading-relaxed">
      <span
        className={`flex-shrink-0 ${isBefore ? 'text-muted' : 'text-[var(--brilliant-azure)]'}`}
      >
        {isBefore ? '-' : '→'}
      </span>
      <span className={isBefore ? 'text-muted' : 'text-foreground'}>
        {children}
      </span>
    </li>
  );
}

function FlowStep({
  num,
  title,
  children,
}: {
  num: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      className="grid grid-cols-[48px_1fr] gap-6 py-8 [&:not(:first-child)]:border-t border-border last:border-b-0"
      variants={fadeUp}
    >
      <div className="flex items-center justify-center w-10 h-10 rounded-full border border-[var(--brilliant-azure)]/30 bg-[var(--brilliant-azure)]/10 text-sm font-semibold text-[var(--brilliant-azure)] mt-1">
        {num}
      </div>
      <div>
        <h3 className="text-xl md:text-2xl font-medium text-foreground mb-2">{title}</h3>
        {children}
      </div>
    </motion.div>
  );
}

function Chip({ children, icon: Icon }: { children: React.ReactNode; icon: LucideIcon }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-white/[0.06] backdrop-blur-2xl border border-white/[0.08] rounded-full text-xs text-muted-foreground px-4 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary/40" tabIndex={0}>
      <Icon className="w-3 h-3" />
      {children}
    </span>
  );
}

function CollabCard({
  question,
  answer,
}: {
  question: string;
  answer: string;
}) {
  return (
    <motion.div
      className="bg-card border border-border rounded-2xl p-6 md:p-8"
      variants={fadeUp}
    >
      <div className="text-base md:text-lg text-primary italic mb-3 leading-relaxed">
        {question}
      </div>
      <div className="text-sm sm:text-base text-muted-foreground leading-relaxed">
        {answer}
      </div>
    </motion.div>
  );
}

function WorthItem({
  num,
  title,
  children,
}: {
  num: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-card p-6 md:p-8">
      <div className="font-display text-4xl md:text-5xl text-primary mb-3">
        {num}
      </div>
      <h3 className="text-base md:text-lg font-medium text-foreground mb-2">{title}</h3>
      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
        {children}
      </p>
    </div>
  );
}

export default Radone;
